import { z } from "zod";
export const permissionSchema = z.enum([
    "READ:TAX_CRM:ALL",
    "MANAGE:TAX_CRM:ALL",
    "READ:TAX_OVERVIEW:ALL",
    "READ:TAX_NEWS:ALL",
    "CREATE:TAX_NEWS:ALL",
    "UPDATE:TAX_NEWS:ALL",
    "CREATE:TAX_EVIDENCE_TASK:ALL",
    "UPDATE:TAX_EVIDENCE_TASK:ALL",
    "READ:TAX_NFE:ALL",
    "CREATE:TAX_NFE:ALL",
    "READ:TAX_RULE:ALL",
    "CREATE:TAX_RULE:ALL",
    "REVIEW:TAX_RULE:ALL",
    "REVIEW:PRODUCT_IMAGE:ALL",
    "PUBLISH:TAX_RULE:ALL",
    "ROLLBACK:TAX_RULESET:ALL",
    "READ:TAX_CALCULATION:ALL",
    "EXECUTE:TAX_CALCULATION:ALL",
    "EXPORT:TAX_CALCULATION:ALL",
    "EXECUTE:TAX_SOURCE_RUN:ALL",
    "MANAGE:TAX_SOURCE:ALL",
    "REPLAY:TAX_DLQ:ALL",
    "READ:TAX_AUDIT:ALL",
    "READ:TAX_INTEGRATION:ALL",
    "MANAGE:TAX_INTEGRATION:ALL",
    "ADMIN:TAX_PLATFORM:ALL",
]);
export const PERMISSIONS = permissionSchema.options;
export const operatorLocaleSchema = z.enum(["pt-BR", "es", "en"]);
export const OPERATOR_LOCALES = operatorLocaleSchema.options;
export const DEFAULT_OPERATOR_LOCALE = "pt-BR";
/**
 * Interactive tax access has five intentional roles. Capabilities remain
 * explicit below; the role is a stable label for operators and audit output,
 * never a substitute for a permission check.
 *
 * - SUPER_ADMIN: full workspace including the users module and invites.
 * - TAX_COMMERCIAL: calculations, lots and operational NF-e only.
 * - TAX_MANAGER: fiscal operations; no user administration or Audit.
 * - TAX_READER: fiscal read surfaces; no Audit, execute, review, publish or manage.
 * - TAX_DEVELOPER: integration keys, consumer docs, MCP tools and Audit. No Users.
 */
export const principalRoleSchema = z.enum([
    "SUPER_ADMIN",
    "TAX_COMMERCIAL",
    "TAX_MANAGER",
    "TAX_READER",
    "TAX_DEVELOPER",
]);
export const PRINCIPAL_ROLES = principalRoleSchema.options;
/** Commercial access is a ceiling as well as a default; persisted grants may be narrower. */
export const TAX_COMMERCIAL_PERMISSIONS = [
    "READ:TAX_CALCULATION:ALL",
    "EXECUTE:TAX_CALCULATION:ALL",
    "EXPORT:TAX_CALCULATION:ALL",
    "READ:TAX_NFE:ALL",
    "CREATE:TAX_NFE:ALL",
];
/** Never infer a missing persisted commercial grant or honor a legacy broad grant. */
export function effectivePermissionsForRole(role, held) {
    return role === "TAX_COMMERCIAL"
        ? held.filter((permission) => TAX_COMMERCIAL_PERMISSIONS.includes(permission))
        : [...held];
}
/** Compatibility is local to NF-e; dedicated grants never open rules or evidence. */
export function canReadTaxNfe(held) {
    return held.some((permission) => ["READ:TAX_NFE:ALL", "READ:TAX_RULE:ALL", "ADMIN:TAX_PLATFORM:ALL"].includes(permission));
}
export function canUploadTaxNfe(held) {
    return held.some((permission) => ["CREATE:TAX_NFE:ALL", "CREATE:TAX_NEWS:ALL", "ADMIN:TAX_PLATFORM:ALL"].includes(permission));
}
export const TAX_READER_PERMISSIONS = [
    "READ:TAX_OVERVIEW:ALL",
    "READ:TAX_NEWS:ALL",
    "READ:TAX_RULE:ALL",
    "READ:TAX_CALCULATION:ALL",
    "READ:TAX_INTEGRATION:ALL",
];
export const TAX_DEVELOPER_PERMISSIONS = [
    "READ:TAX_OVERVIEW:ALL",
    "READ:TAX_RULE:ALL",
    "READ:TAX_CALCULATION:ALL",
    "READ:TAX_AUDIT:ALL",
    "READ:TAX_INTEGRATION:ALL",
    "MANAGE:TAX_INTEGRATION:ALL",
];
export const TAX_MANAGER_PERMISSIONS = PERMISSIONS.filter((permission) => permission !== "ADMIN:TAX_PLATFORM:ALL" &&
    permission !== "READ:TAX_AUDIT:ALL" &&
    permission !== "READ:TAX_CRM:ALL" &&
    permission !== "MANAGE:TAX_CRM:ALL");
export const SUPER_ADMIN_PERMISSIONS = PERMISSIONS;
export function permissionsForRole(role) {
    switch (role) {
        case "SUPER_ADMIN":
            return [...SUPER_ADMIN_PERMISSIONS];
        case "TAX_COMMERCIAL":
            return [...TAX_COMMERCIAL_PERMISSIONS];
        case "TAX_MANAGER":
            return [...TAX_MANAGER_PERMISSIONS];
        case "TAX_READER":
            return [...TAX_READER_PERMISSIONS];
        case "TAX_DEVELOPER":
            return [...TAX_DEVELOPER_PERMISSIONS];
    }
}
/** @deprecated Use TAX_MANAGER_PERMISSIONS. Kept for older mock fixtures. */
export const TAX_OPERATOR_DEFAULT_PERMISSIONS = TAX_MANAGER_PERMISSIONS;
export const principalAccessStatusSchema = z.enum(["ACTIVE", "PENDING"]);
/**
 * How an operator row came to exist, when it was not an invite or the
 * bootstrap job. `GOOGLE_AUTO`: created just in time by a verified Google
 * Workspace sign-in with the least-privileged role (TAX_READER).
 */
export const principalOriginSchema = z.enum(["GOOGLE_AUTO"]);
/** The role a just-in-time Google principal receives until an admin changes it. */
export const GOOGLE_AUTO_PROVISION_ROLE = "TAX_READER";
export const principalAccessRecordSchema = z
    .object({
    id: z.string().uuid(),
    status: principalAccessStatusSchema,
    email: z.string().email(),
    displayName: z.string().min(1).optional(),
    role: principalRoleSchema,
    locale: operatorLocaleSchema,
    permissions: z.array(permissionSchema),
    organizationIds: z.array(z.string().uuid()),
    active: z.boolean(),
    /** Present only for principals created by Google sign-in. */
    origin: principalOriginSchema.optional(),
    /** A Google account is pinned to this operator (first Google sign-in). */
    googleLinked: z.boolean().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const rulesetStatusSchema = z.enum([
    "DRAFT",
    "IN_REVIEW",
    "APPROVED",
    "PUBLISHED",
    "SUPERSEDED",
    "REJECTED",
]);
export const ruleProposalStatusSchema = z.enum([
    "PENDING_REVIEW",
    "APPROVED",
    "REJECTED",
    "PUBLISHED",
]);
export const sourceStatusSchema = z.enum(["ACTIVE", "PAUSED", "DEGRADED", "DISABLED"]);
export const ingestionRunStatusSchema = z.enum([
    "QUEUED",
    "DISCOVERING",
    "FETCHING",
    "EXTRACTING",
    "SUCCEEDED",
    "PARTIAL",
    "FAILED",
    "CANCELLED",
]);
export const sessionSchema = z
    .object({
    principalId: z.string().uuid(),
    cognitoSub: z.string().min(1),
    userId: z.string().min(1),
    email: z.string().email(),
    displayName: z.string().min(1).optional(),
    /** Optional OIDC avatar claim. It is display-only and never used for authorization. */
    avatarUrl: z.string().url().optional(),
    role: principalRoleSchema.or(z.string().min(1)),
    locale: operatorLocaleSchema.optional(),
    permissions: z.array(permissionSchema),
    organizationScopes: z.array(z.string().uuid()),
    environment: z.enum(["dev", "stage", "prod"]),
})
    .strict();
/** Audit belongs to Developers; legacy grants never override the role boundary. */
export function canReadTaxAudit(role, permissions) {
    return ((role === "SUPER_ADMIN" && permissions.includes("ADMIN:TAX_PLATFORM:ALL")) ||
        (role === "TAX_DEVELOPER" && permissions.includes("READ:TAX_AUDIT:ALL")));
}
//# sourceMappingURL=governance.js.map